import { useState } from "react";

import { AlertTriangle, CheckCircle2, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { STAFF_EMAIL_DOMAIN } from "@/features/staff/schemas/staff";
import {
  useLegacyAdminAccounts,
  useMigrateAccountsToStaff,
} from "@/features/staff/hooks/useStaff";

/**
 * Migración única de las cuentas de equipo hacia la colección `staff`.
 *
 * Se usa MIENTRAS el gate antiguo sigue activo: primero se revisa la lista y se
 * comprueba que no falta nadie, y solo después se cambia la puerta de entrada.
 * Ese orden es lo que evita que alguien se quede fuera del panel.
 */
export function StaffMigrationPanel() {
  const [expanded, setExpanded] = useState(false);
  const { data: candidates = [], isLoading } = useLegacyAdminAccounts(expanded);
  const migrate = useMigrateAccountsToStaff();

  const migratable = candidates.filter((candidate) => candidate.migratable);
  const blocked = candidates.filter(
    (candidate) => !candidate.migratable && !candidate.alreadyMigrated
  );
  const done = candidates.filter((candidate) => candidate.alreadyMigrated);

  const handleMigrate = async () => {
    try {
      const result = await migrate.mutateAsync(migratable);

      if (result.migrated.length > 0) {
        toast.success(`${result.migrated.length} cuenta(s) migradas al equipo`);
      }
      if (result.failed.length > 0) {
        toast.error(
          `No se migraron ${result.failed.length}: ${result.failed
            .map((failure) => `${failure.email} (${failure.reason})`)
            .join(", ")}`
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "No se pudo completar la migración"
      );
    }
  };

  if (!expanded) {
    return (
      <Card>
        <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-medium">Migrar cuentas de equipo</p>
            <p className="text-sm text-muted-foreground">
              Revisa quién entra hoy con el acceso antiguo y crea sus perfiles de equipo
              antes de cambiar la puerta de entrada.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setExpanded(true)}>
            Revisar cuentas
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        <div>
          <p className="font-medium">Cuentas con acceso antiguo</p>
          <p className="text-sm text-muted-foreground">
            Solo se pueden migrar las que ya usan un correo{" "}
            <code className="text-xs">{STAFF_EMAIL_DOMAIN}</code>. Las demás necesitan una
            cuenta nueva bajo ese dominio: <strong>al cambiar el gate se quedarían
            fuera</strong>.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {blocked.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950">
                <p className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                  <AlertTriangle className="h-4 w-4" />
                  {blocked.length} cuenta(s) se quedarían fuera
                </p>
                <ul className="mt-2 space-y-1 text-sm text-amber-900 dark:text-amber-200">
                  {blocked.map((candidate) => (
                    <li key={candidate.uid}>
                      {candidate.name} — <code className="text-xs">{candidate.email}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <ul className="space-y-2">
              {candidates.map((candidate) => (
                <li
                  key={candidate.uid}
                  className="flex items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{candidate.name}</p>
                    <code className="text-xs text-muted-foreground">
                      {candidate.email}
                    </code>
                  </div>

                  {candidate.alreadyMigrated ? (
                    <Badge variant="secondary" className="shrink-0 gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      En el equipo
                    </Badge>
                  ) : candidate.hasCorporateEmail ? (
                    <Badge variant="outline" className="shrink-0">
                      Lista para migrar
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="shrink-0">
                      Correo no corporativo
                    </Badge>
                  )}
                </li>
              ))}
            </ul>

            {candidates.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Ninguna cuenta usa el acceso antiguo.
              </p>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                {done.length} en el equipo · {migratable.length} por migrar ·{" "}
                {blocked.length} bloqueadas
              </p>

              <Button
                size="sm"
                onClick={() => void handleMigrate()}
                disabled={migratable.length === 0 || migrate.isPending}
              >
                {migrate.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="mr-2 h-4 w-4" />
                )}
                Migrar {migratable.length} cuenta(s)
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
